import { emptySplitApi } from '../../../../store/emptyApi';
import type {
  ProvenanceRecord,
  ProvenanceRecordDetail,
  ProvenanceReviewRequest,
  BatchReviewRequest,
  BatchDeleteRequest,
  ProvenanceListResponse,
} from '../types/provenance.types';

export enum ProvenanceApiTags {
  ProvenanceRecord = 'ProvenanceRecord',
}

const provenanceApi = emptySplitApi.injectEndpoints({
  endpoints: (build) => ({
    getProvenanceQueue: build.query<ProvenanceListResponse, { status?: string; page?: number; page_size?: number; grouped?: boolean }>({
      query: (params) => ({
        url: '/api/admin/v1/provenance/',
        params: {
          status: params.status || 'queued',
          page: params.page || 1,
          page_size: params.page_size || 20,
          grouped: params.grouped ? 'true' : 'false',
        },
      }),
      providesTags: [ProvenanceApiTags.ProvenanceRecord],
    }),
    getProvenanceRecord: build.query<ProvenanceRecordDetail, string>({
      query: (id) => ({
        url: `/api/admin/v1/provenance/${id}/`,
      }),
      providesTags: (result, error, id) => [{ type: ProvenanceApiTags.ProvenanceRecord, id }],
    }),
    reviewProvenanceRecord: build.mutation<{ id: string; action: string; reviewed_at: string }, { id: string; review: ProvenanceReviewRequest }>({
      query: ({ id, review }) => ({
        url: `/api/admin/v1/provenance/${id}/review/`,
        method: 'POST',
        body: review,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: ProvenanceApiTags.ProvenanceRecord, id },
        ProvenanceApiTags.ProvenanceRecord,
      ],
    }),
    batchReviewProvenanceRecords: build.mutation<{ batch_id: string; reviews_created: number; repository_files_created: number; action: string }, BatchReviewRequest>({
      query: (data) => ({
        url: '/api/admin/v1/provenance/batch_review/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [ProvenanceApiTags.ProvenanceRecord],
    }),
    getProvenanceAnalytics: build.query<Record<string, any>, { days?: number }>({
      query: (params) => ({
        url: '/api/admin/v1/provenance/analytics/',
        params: {
          days: params.days || 30,
        },
      }),
    }),
    deleteProvenanceRecord: build.mutation<{ message: string; id: string }, string>({
      query: (id) => ({
        url: `/api/admin/v1/provenance/${id}/delete_record/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: ProvenanceApiTags.ProvenanceRecord, id },
        ProvenanceApiTags.ProvenanceRecord,
      ],
    }),
    batchDeleteProvenanceRecords: build.mutation<{ message: string; deleted_ids: string[]; count: number }, BatchDeleteRequest>({
      query: (data) => ({
        url: '/api/admin/v1/provenance/batch_delete/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [ProvenanceApiTags.ProvenanceRecord],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetProvenanceQueueQuery,
  useGetProvenanceRecordQuery,
  useReviewProvenanceRecordMutation,
  useBatchReviewProvenanceRecordsMutation,
  useGetProvenanceAnalyticsQuery,
  useDeleteProvenanceRecordMutation,
  useBatchDeleteProvenanceRecordsMutation,
} = provenanceApi;

export { provenanceApi };
